import { useState, useEffect } from 'react';

import {
  Box,
  Table,
  Tooltip,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  CircularProgress,
} from '@mui/material';

import { fDate } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';

import { useAppDispatcher } from 'src/redux/store';
import { getChequeTimeline } from 'src/redux/slices/cheque';

import Label from 'src/components/label';
import Scrollbar from 'src/components/scrollbar';
import { TableHeadCustom } from 'src/components/table';

import { ChequeStatus, IChequeTimeline, IChequeTimelineEvent } from 'src/types/cheque';

type Props = {
  chequeId: string;
  // Reload when this changes (status changed, payment or settlement added).
  refreshKey: unknown;
  // Money columns only make sense once the cheque has been returned.
  showMoney: boolean;
};

const STATUS_COLOR = (status?: ChequeStatus) =>
  (status === ChequeStatus.PAID && 'success') ||
  (status === ChequeStatus.RETURN && 'error') ||
  (status === ChequeStatus.SETTLED && 'warning') ||
  (status === ChequeStatus.RECOVERING && 'warning') ||
  (status === ChequeStatus.RECOVERED && 'success') ||
  (status === ChequeStatus.HANDOVER && 'info') ||
  (status === ChequeStatus.HOLDING && 'warning') ||
  (status === ChequeStatus.BANKING && 'info') ||
  (status === ChequeStatus.FACTORING && 'secondary') ||
  (status === ChequeStatus.FACTORED && 'primary') ||
  'default';

function EventLabel({ event }: { event: IChequeTimelineEvent }) {
  switch (event.type) {
    case 'RECOVERY':
      return (
        <Label variant="soft" color="success">
          Recovered from debtor
        </Label>
      );
    case 'DIRECT_TO_SUPPLIER':
      return (
        <Label variant="soft" color="info">
          Debtor paid supplier directly
        </Label>
      );
    case 'SETTLEMENT':
      return (
        <Label variant="soft" color="warning">
          Company paid supplier
        </Label>
      );
    default:
      return (
        <Label variant="soft" color={STATUS_COLOR(event.status)}>
          {event.status?.split('_').join(' ')}
        </Label>
      );
  }
}

function AmountCell({ event }: { event: IChequeTimelineEvent }) {
  if (event.amountInCents !== undefined) {
    return <>{fCurrencyRupees(event.amountInCents)}</>;
  }
  if (event.amountNotRecorded) {
    return (
      <Tooltip title="This was done before payment amounts were tracked">
        <Typography variant="caption" color="text.disabled">
          not recorded
        </Typography>
      </Tooltip>
    );
  }
  return <>-</>;
}

// Two short figures: what the debtor still owes us, and what we still owe the
// supplier. The full picture (incl. company net position) is on the
// Supplier Settlement tab.
function Balances({ event }: { event: IChequeTimelineEvent }) {
  const b = event.balances;
  return (
    <Box sx={{ typography: 'caption', lineHeight: 1.6, whiteSpace: 'nowrap' }}>
      <div>To recover: {fCurrencyRupees(b.recoveryOutstandingInCents)}</div>
      <div>Owed to supplier: {fCurrencyRupees(b.settlementOutstandingInCents)}</div>
    </Box>
  );
}

export default function ChequeTimeline({ chequeId, refreshKey, showMoney }: Props) {
  const dispatch = useAppDispatcher();
  const [timeline, setTimeline] = useState<IChequeTimeline | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    dispatch(getChequeTimeline({ chequeId }))
      .unwrap()
      .then((res) => !cancelled && setTimeline(res))
      .catch(() => !cancelled && setTimeline(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [dispatch, chequeId, refreshKey]);

  if (loading && !timeline) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Newest first, like the table this replaces.
  const events = [...(timeline?.events ?? [])].reverse();

  const head = [
    { id: 'date', label: 'Date', align: 'left' as const, primary: true },
    { id: 'event', label: 'Event', align: 'left' as const, primary: true },
    ...(showMoney
      ? [{ id: 'amount', label: 'Amount', align: 'right' as const, primary: true }]
      : []),
    { id: 'details', label: 'Holder / Note', align: 'left' as const, primary: true },
    ...(showMoney
      ? [{ id: 'balances', label: 'Balance After', align: 'left' as const, primary: true }]
      : []),
    { id: 'by', label: 'By', align: 'right' as const, primary: true },
  ];

  return (
    <TableContainer sx={{ position: 'relative', overflow: 'hidden', maxHeight: 520 }}>
      <Scrollbar>
        <Table stickyHeader size="small" sx={{ minWidth: 960 }}>
          <TableHeadCustom headLabel={head} />

          <TableBody>
            {events.map((event) => (
              <TableRow hover key={`${event.type}-${event.id}`}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fDate(event.date)}</TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <EventLabel event={event} />
                </TableCell>

                {showMoney && (
                  <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <AmountCell event={event} />
                  </TableCell>
                )}

                <TableCell>
                  {event.type === 'STATUS' ? event.holder : null}
                  {event.type === 'DIRECT_TO_SUPPLIER' && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      Counts as a recovery and a supplier settlement
                    </Typography>
                  )}
                  {event.note && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      {event.note}
                    </Typography>
                  )}
                </TableCell>

                {showMoney && (
                  <TableCell>
                    <Balances event={event} />
                  </TableCell>
                )}

                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                  {event.by?.firstName}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
    </TableContainer>
  );
}
